import {
  type CSSProperties,
  memo,
  type SVGAttributes,
  useEffect,
  useMemo,
  useState,
} from "react";

import { type TenantAssetClassLogoKey, tenantConfig } from "@tenant-config";
import { AssetClass } from "@vega-api/generated/types/assetClass";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { default as CreditLogo } from "./credit.svg?react";
import { default as EquityLogo } from "./equity.svg?react";
import { default as HybridLogo } from "./hybrid.svg?react";
import { default as RealEstateLogo } from "./real-estate.svg?react";

/**
 * This is a workaround for Safari's rendering issue with SVGs.
 * It forces a re-render of the SVG by changing the key whenever the assetClass changes.
 * See discussion: https://vegainvestments.slack.com/archives/C06QSCQPB37/p1743765227417689
 */

const isSafariBrowser = (() => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();

  return ua.includes("safari") && !ua.includes("chrome");
})();

const SAFARI_STYLES: CSSProperties = {
  WebkitTransform: "translateZ(0)",
  WebkitBackfaceVisibility: "hidden",
  WebkitFilter: "blur(0)",
  transform: "translateZ(0)",
  backfaceVisibility: "hidden",
  filter: "blur(0)",
};

const getTenantLogo = (assetClass?: string) => {
  const assetClassLogos = tenantConfig.branding.assets?.assetClassLogos;

  if (!assetClassLogos) return undefined;

  const logoKey = (assetClass ??
    AssetClass.PRIVATE_EQUITY) as TenantAssetClassLogoKey;

  return assetClassLogos[logoKey];
};

const getTenantLogoBackground = (assetClass?: string) => {
  const assetClassLogoBackgrounds =
    tenantConfig.branding.assets?.assetClassLogoBackgrounds;

  if (!assetClassLogoBackgrounds) return undefined;

  const logoKey = (assetClass ??
    AssetClass.PRIVATE_EQUITY) as TenantAssetClassLogoKey;

  return assetClassLogoBackgrounds[logoKey];
};

const getLogo = (assetClass?: string) => {
  switch (assetClass) {
    case AssetClass.PRIVATE_CREDIT:
      return CreditLogo;
    case AssetClass.HYBRID:
      return HybridLogo;
    case AssetClass.REAL_ASSETS:
      return RealEstateLogo;
    case AssetClass.PRIVATE_EQUITY:
    default:
      return EquityLogo;
  }
};

const getLogoColor = (assetClass?: string) => {
  switch (assetClass) {
    case AssetClass.PRIVATE_CREDIT:
      return "text-data-1";
    case AssetClass.HYBRID:
      return "text-data-2";
    case AssetClass.REAL_ASSETS:
      return "text-data-5";
    case AssetClass.PRIVATE_EQUITY:
    default:
      return "text-data-3";
  }
};

const getLogoBackgroundClass = (assetClass?: string) => {
  switch (assetClass) {
    case AssetClass.PRIVATE_CREDIT:
      return "bg-data-1";
    case AssetClass.HYBRID:
      return "bg-data-2";
    case AssetClass.REAL_ASSETS:
      return "bg-data-5";
    case AssetClass.PRIVATE_EQUITY:
    default:
      return "bg-data-3";
  }
};

export const FundLogos = memo(
  ({
    assetClass,
    isLoading = false,
    bigSize = false,
    ...props
  }: SVGAttributes<SVGSVGElement> & {
    assetClass?: string;
    isLoading?: boolean;
    bigSize?: boolean;
  }) => {
    const tenantLogo = getTenantLogo(assetClass);
    const Logo = tenantLogo ?? getLogo(assetClass);
    const logoColor = getLogoColor(assetClass);
    const logoBackground = getTenantLogoBackground(assetClass);
    const [renderKey, setRenderKey] = useState(0);

    useEffect(() => {
      if (isSafariBrowser) {
        setRenderKey((prevKey) => prevKey + 1);
      }
    }, [assetClass]);

    const containerClass = useMemo(
      () =>
        cnTwMerge("rounded-default overflow-hidden size-8 min-w-8", {
          "skeleton-box": isLoading,
          "sm:size-[3.75em]": bigSize,
          [getLogoBackgroundClass(assetClass)]: Boolean(
            tenantLogo && !logoBackground,
          ),
        }),
      [assetClass, isLoading, bigSize, logoBackground, tenantLogo],
    );
    const containerStyle: CSSProperties | undefined = logoBackground
      ? { backgroundColor: logoBackground }
      : undefined;

    if (isSafariBrowser) {
      return (
        <div className={containerClass} style={containerStyle}>
          <Logo
            className={cnTwMerge("size-full", logoColor)}
            key={`${assetClass || "default"}-${renderKey}`}
            {...props}
            style={{
              ...props.style,
              ...SAFARI_STYLES,
            }}
          />
        </div>
      );
    }

    return (
      <div className={containerClass} style={containerStyle}>
        <Logo className={cnTwMerge("size-full", logoColor)} {...props} />
      </div>
    );
  },
);
